import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePackingListStore } from 'stores/packingList/packingList';
import { formatCalendarDay } from 'support/helpers/dateTime/dateTime';
import { isLineItemInPlaceholderUnit } from 'support/helpers/packingList/defaultTransportUnit';
import { readQuantity } from 'support/helpers/packingList/packableQuantity';
import { WebEDIRowData } from 'support/types/webediTable';

import { Summary } from '../types';

type ShipmentSummaryValues = {
  totalQuantity: number;
  lineItemCount: number;
  deliveryDate?: number;
  notSentLineItemCount: number;
};

type ShipmentLineItem = {
  shipped_quantity: number;
};

// Shared by the shipping notice and the dispatch response: both confirm a shipment off the same
// canonical fields (`line_items[].shipped_quantity`, `transport_units`, `expected_delivery_date`),
// so they get one summary rather than two that could drift.
export const useShipmentSummaryValues = (): ShipmentSummaryValues => {
  const packingListEnabled = usePackingListStore((state) => state.enabled);
  const [lineItems, expectedDeliveryDate] = useWatch({
    name: ['line_items', 'info.0.expected_delivery_date'],
  });
  const transportUnits = useWatch({ name: 'transport_units', disabled: !packingListEnabled }) as
    | Array<WebEDIRowData>
    | undefined;

  const sentLineItems = packingListEnabled
    ? lineItems?.filter((lineItem: WebEDIRowData) => !isLineItemInPlaceholderUnit(lineItem, transportUnits ?? []))
    : lineItems;
  const confirmedLineItems = sentLineItems?.filter((lineItem: ShipmentLineItem) => lineItem.shipped_quantity > 0);

  return useMemo(
    () => ({
      totalQuantity: calculateLineItemsQuantity(sentLineItems),
      lineItemCount: confirmedLineItems?.length ?? 0,
      deliveryDate: expectedDeliveryDate,
      notSentLineItemCount: packingListEnabled ? (lineItems?.length ?? 0) - (sentLineItems?.length ?? 0) : 0,
    }),
    [sentLineItems, confirmedLineItems?.length, expectedDeliveryDate, packingListEnabled, lineItems?.length],
  );
};

export const useShipmentSummary = (): Summary => {
  const { t } = useTranslation();
  const values = useShipmentSummaryValues();

  return useMemo(() => {
    return {
      content: [
        [
          {
            label: t('webedi:documentSummary.shipment.shippedLineItemCount.label'),
            value: Math.round(values.lineItemCount),
          },
          ...(values.notSentLineItemCount > 0
            ? [
                {
                  label: t('webedi:documentSummary.shipment.notSentLineItemCount.label'),
                  value: Math.round(values.notSentLineItemCount),
                  test: (mode: 'create-document' | 'send-confirmation-modal' | 'view-document') =>
                    mode === 'send-confirmation-modal',
                  emphasis: 'warning' as const,
                },
              ]
            : []),
          {
            label: t('webedi:documentSummary.shipment.totalQuantity.label'),
            value: Math.round(values.totalQuantity),
          },
          {
            label: t('webedi:documentSummary.shipment.deliveryDate.label'),
            value: formatCalendarDay(values.deliveryDate) || '—',
          },
        ],
      ],
    };
  }, [t, values]);
};

// `?? 0` alone is not enough: an empty numeric WebEDI cell registers as NaN (valueAsNumber), which
// is not nullish. One blank quantity poisoned the whole reduce, and the trailing `|| 0` then turned
// that NaN into a plausible-looking 0 — the total silently lost every other line.
const calculateLineItemsQuantity = (lineItems: Array<any> | undefined): number => {
  return (
    lineItems?.reduce((acc, item) => {
      if (item.line_items?.length) {
        return acc + calculateLineItemsQuantity(item.line_items);
      }

      return acc + readQuantity(item.shipped_quantity);
    }, 0) || 0
  );
};
